import React, { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { getDirtyValues, isFormDirty } from "@/lib/utils";
import { parseRead } from "@/lib/zod/read-schema";
import { startDateForPicker } from "@/pages/performances/lib/performance-start-date";
import { ScreeningMovieField } from "@/pages/screenings/components/fields/screening-movie-field";
import { ScreeningVenueField } from "@/pages/screenings/components/fields/screening-venue-field";
import { useScreeningRepository } from "@/pages/screenings/hooks/use-screening-repository";
import { screeningStartForSubmit } from "@/pages/screenings/lib/screening-start-date";
import {
  ScreeningEditFormType,
  screeningEditSchema,
} from "@/pages/screenings/schemas/screening.edit.form";
import { useVenueRepository } from "@/pages/venues/hooks/use-venue-repository";

const schemaProvider = new ZodProvider(screeningEditSchema);

/**
 * Edit one screening: its movie, cinema, time, format, screen, prices and ticket link.
 *
 * The start time is the venue's wall clock. The DateTimePicker holds browser-local components,
 * so the stored instant is restamped through the venue timezone on the way in and out (the
 * `PerformancesEdit` helpers). A screening carries its venue's `timezone` on the denormalized
 * copy, so no venue read is needed — unless the curator picks another venue, whose zone then
 * decides how the picker's wall clock is saved. Re-pointing to a venue in another zone keeps the
 * shown time: 20:00 stays 20:00 at the new cinema.
 *
 * No delete button: the stub this replaced had none, and deleting is not what this page is for.
 */
export const ScreeningsEdit = () => {
  const { get, update } = useScreeningRepository();
  const { get: getVenue } = useVenueRepository();
  const { id } = useParsed();
  const [values, setValues] = React.useState<ScreeningEditFormType>();
  const [timezone, setTimezone] = React.useState<string | undefined>();
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();
  const setTitle = useDocumentTitle();

  useEffect(() => {
    if (!id) {
      open?.({
        type: "error",
        message: "Something went wrong. This shouldn't be happening",
      });
      return;
    }
    setLoading(true);
    get(id.toString())
      .then((response) => {
        const raw = response as Record<string, any>;
        const zone: string | undefined = raw?.venue?.timezone || undefined;
        const data = parseRead(
          screeningEditSchema,
          {
            ...raw,
            startDate: startDateForPicker(raw?.startDate, zone),
            endDate: startDateForPicker(raw?.endDate, zone) ?? null,
          },
          "screening",
        ) as ScreeningEditFormType;
        setTimezone(zone);
        setValues(data);
        setTitle("Edit " + (raw?.movie?.title ?? "screening"));
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get screening",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const initializeForm = (
    form: UseFormReturn<ScreeningEditFormType, any, ScreeningEditFormType>,
  ) => {
    form.watch((value) => {
      setWarnWhen(
        form.formState.isDirty &&
          isFormDirty(
            form.formState.dirtyFields as Record<string, unknown>,
            value as Record<string, unknown>,
            form.formState.defaultValues as Record<string, unknown>,
          ),
      );
    });
  };

  /** A newly picked venue's zone: from the search hit, else from the venue document. */
  const zoneOfPickedVenue = async (
    venue: NonNullable<ScreeningEditFormType["venue"]>,
  ): Promise<string | undefined> => {
    if (venue.timezone) return venue.timezone;
    try {
      return (await getVenue(venue.id))?.timezone || undefined;
    } catch (error) {
      console.error("venue read for timezone failed", error);
      return undefined;
    }
  };

  const onSubmit = async (
    allValues: ScreeningEditFormType,
    form: UseFormReturn<ScreeningEditFormType, any, ScreeningEditFormType>,
  ) => {
    const changed = getDirtyValues(
      form.formState.dirtyFields as Record<string, unknown>,
      allValues,
    ) as Partial<ScreeningEditFormType>;
    delete changed.endDate;

    const startDate = screeningStartForSubmit({
      pickerValue: allValues.startDate,
      startChanged: changed.startDate !== undefined,
      originalZone: timezone,
      pickedZone: changed.venue
        ? await zoneOfPickedVenue(changed.venue)
        : undefined,
    });
    if (startDate === undefined) delete changed.startDate;
    else changed.startDate = startDate;

    setWarnWhen(false);
    await update(
      { id: allValues.id, ...changed },
      allValues.movie?.title ?? undefined,
    );
  };

  return (
    <div className="flex flex-row">
      <div className="flex w-full flex-col space-y-4 p-8">
        {values && !timezone && (
          <div
            role="alert"
            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm"
          >
            This screening&apos;s venue has no timezone, so the start time is
            shown and saved in your own timezone. Set the venue timezone before
            changing it.
          </div>
        )}
        {values && (
          <AutoForm
            schema={schemaProvider}
            defaultValues={values}
            onSubmit={onSubmit}
            onFormInit={initializeForm}
            {...injectCustomAutoFormComponents({
              formComponents: {
                screeningMovie: ScreeningMovieField,
                screeningVenue: ScreeningVenueField,
              },
            })}
          >
            <BottomActionBar>
              <Button disabled={!warnWhen} type="submit">
                Submit
              </Button>
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
    </div>
  );
};
